// The forms of Logistics → Settings and the labels of its packages. The API checks the same rules
// (common/logistics in sportya-api) and decides; the page only spares a request it would refuse.
export const PACKAGE_NAME_LIMIT = 60
export const SIDE_LIMIT = 600
export const WEIGHT_LIMIT = 100
// Cargus takes an envelope of up to 1 kg, so its own weight is no more.
export const ENVELOPE_WEIGHT_LIMIT = 1
export const TEAM_NAME_LIMIT = 120
export const FIELD_LIMIT = 200
export const LINK_LIMIT = 500
export const AWB = "{awb}"
export const TEAM_LANGUAGES = [["ro", "Romanian"], ["en", "English"], ["bg", "Bulgarian"]]
// The Cargus tracking link: the Romanian page, and the one for every other language.
export const TRACKING_LANGUAGES = [["ro", "Romanian"], ["other", "Other languages"]]
export const PACKAGE_TYPES = [["box", "Box"], ["envelope", "Envelope · documents only"]]
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const typeLabel = shippingPackage => Object.fromEntries(PACKAGE_TYPES)[shippingPackage.type]
export const dimensionsLabel = ({ length, width, height }) => `${[length, width, height].filter(side => side != null).join(" × ")} cm`
export const weightLabel = ({ weight }) => `${weight} kg`
// The default package splits a shipment's goods into parcels, so it is an active box: an envelope carries only documents.
export const canBeDefault = shippingPackage => shippingPackage.active && shippingPackage.type === "box"

// ——— The team's contact and the Cargus link: each card saves its fields whole ———
export const contactForm = contact => ({ ro: contact?.names.ro || "", en: contact?.names.en || "", bg: contact?.names.bg || "",
  phone: contact?.phone || "", email: contact?.email || "" })
export const contactOf = form => ({ names: { ro: form.ro.trim(), en: form.en.trim(), bg: form.bg.trim() }, phone: form.phone.trim(),
  email: form.email.trim() })
export const trackingForm = tracking => Object.fromEntries(TRACKING_LANGUAGES.map(([language]) => [language, tracking?.[language] || ""]))
export const trackingOf = form => Object.fromEntries(TRACKING_LANGUAGES.map(([language]) => [language, form[language].trim()]))
// Whether a form still holds what is saved, once trimmed: nothing to save then.
const same = (first, second) => JSON.stringify(first) === JSON.stringify(second)
export const sameContact = (form, contact) => same(contactOf(form), contactOf(contactForm(contact)))
export const sameTracking = (form, tracking) => same(trackingOf(form), trackingOf(trackingForm(tracking)))

// What the contact card must fix before it is saved, by field.
export const contactErrors = form => {
  const errors = {}
  for (const [language, name] of TEAM_LANGUAGES) {
    if (!form[language].trim() || form[language].trim().length > TEAM_NAME_LIMIT) errors[language] = `Enter the team's name in ${name}.`
  }
  if (!form.phone.trim() || form.phone.trim().length > FIELD_LIMIT) errors.phone = "Enter a phone number."
  if (!EMAIL.test(form.email.trim()) || form.email.trim().length > FIELD_LIMIT) errors.email = "Enter a valid email."
  return errors
}
// A tracking page's template: https, with the AWB's place once, after the site's address.
const isTrackingTemplate = value => {
  const link = value.trim()
  const host = /^https:\/\/[^/?#]+/i.exec(link)?.[0]
  const at = link.indexOf(AWB)
  return Boolean(host) && link.length <= LINK_LIMIT && at >= host.length && !link.includes(AWB, at + 1)
}
export const trackingErrors = form => Object.fromEntries(TRACKING_LANGUAGES.filter(([language]) => !isTrackingTemplate(form[language]))
  .map(([language]) => [language, `Enter an https:// link with ${AWB} once, after the site's address.`]))

// ——— A shipping package, as its dialog edits it ———
export const packageForm = shippingPackage => shippingPackage
  ? { name: shippingPackage.name, type: shippingPackage.type, length: String(shippingPackage.length), width: String(shippingPackage.width),
    height: shippingPackage.height == null ? "" : String(shippingPackage.height), weight: String(shippingPackage.weight), active: shippingPackage.active }
  : { name: "", type: "box", length: "", width: "", height: "", weight: "", active: true }
const numberOf = value => (String(value).trim() === "" ? NaN : Number(value))
const isSide = value => Number.isInteger(numberOf(value)) && numberOf(value) >= 1 && numberOf(value) <= SIDE_LIMIT
const sidesOf = form => (form.type === "envelope" ? ["length", "width"] : ["length", "width", "height"])
// What the dialog must fix before a command is sent, by field; another package's name is taken whatever its case.
export const packageErrors = (form, packages, packageId) => {
  const errors = {}
  const name = form.name.trim()
  if (!name || name.length > PACKAGE_NAME_LIMIT) errors.name = `Enter a name of up to ${PACKAGE_NAME_LIMIT} characters.`
  else if (packages.some(other => other.id !== packageId && other.name.toLowerCase() === name.toLowerCase())) errors.name = "Another package has this name."
  for (const field of sidesOf(form)) if (!isSide(form[field])) errors[field] = `Enter whole centimetres from 1 to ${SIDE_LIMIT}.`
  const weight = numberOf(form.weight)
  const envelope = form.type === "envelope"
  if (!(weight >= 0 && weight <= (envelope ? ENVELOPE_WEIGHT_LIMIT : WEIGHT_LIMIT))) {
    errors.weight = envelope ? `Enter a weight from 0 to ${ENVELOPE_WEIGHT_LIMIT} kg for an envelope.` : `Enter a weight from 0 to ${WEIGHT_LIMIT} kg.`
  }
  return errors
}
export const packageOf = form => ({ name: form.name.trim(), type: form.type, length: Number(form.length), width: Number(form.width),
  height: form.type === "envelope" ? null : Number(form.height), weight: Number(form.weight), active: form.active })
// Whether the dialog still holds the package as saved: nothing to save then.
export const samePackage = (form, shippingPackage) => Boolean(shippingPackage)
  && same(packageOf(form), packageOf(packageForm(shippingPackage)))
